import { Dialog, type DialogPassThroughOptions } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import {
    createContext,
    useContext,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { ToastContent } from '../../Toast/ToastContent';
import { useDoubleEscape } from '../hooks/useDoubleEscape';

interface DialogContextValue {
    close: () => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function useDialogClose(): () => void {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error(
            'useDialogClose must be used within DialogWithDoubleEscape',
        );
    }
    return context.close;
}

interface DialogWithDoubleEscapeProps {
    onClose: () => void;
    onBeforeClose?: () => void;
    doubleEscapeEnabled?: boolean;
    children: ReactNode;
    className?: string;
    header?: ReactNode;
    footer?: ReactNode;
    pt?: DialogPassThroughOptions;
    [key: string]: unknown;
}

export function DialogWithDoubleEscape({
    onClose,
    onBeforeClose,
    doubleEscapeEnabled = true,
    children,
    className,
    header,
    footer,
    pt,
    ...dialogProps
}: DialogWithDoubleEscapeProps): ReactNode {
    const [visible, setVisible] = useState(true);
    const toastRef = useRef<Toast>(null);

    const actualClose = (): void => {
        setVisible(false);
        onClose();
    };

    const close = (): void => {
        if (onBeforeClose) {
            onBeforeClose();
        } else {
            actualClose();
        }
    };

    useDoubleEscape({
        onClose: close,
        onShowFeedback: (timeWindow) => {
            toastRef.current?.show({
                severity: 'info',
                summary: 'Info',
                detail: 'Press ESC again to close',
                content: ToastContent,
                life: timeWindow,
            });
        },
        enabled: doubleEscapeEnabled,
    });

    const handleHide = (): void => {
        if (!visible) {
            return;
        }
        close();
    };

    return (
        <DialogContext.Provider value={{ close }}>
            <Toast ref={toastRef} position="top-center" baseZIndex={100} />
            <Dialog
                {...dialogProps}
                visible={visible}
                modal
                draggable={false}
                focusOnShow={false}
                closeOnEscape={false}
                className={className}
                header={header}
                footer={footer}
                closable={false}
                onHide={handleHide}
                pt={pt}
            >
                {children}
            </Dialog>
        </DialogContext.Provider>
    );
}
